// noinspection PointlessArithmeticExpressionJS

import { Box, BoxPosition, ProgramSettings } from '../../generated/model/models'
import { bottomSide, BoxValue, leftSide, rightSide, topSide } from './boxWrapper'
import * as utils from '@/libs/pattern/utils'
import { BoxDef, fullHeight, fullWidth } from '@/libs/pattern/boxDef'
import { PalletDef } from '@/libs/pattern/palletDef'
import { Borders, Range } from '@/generated/model/gripper'
import { MachineData } from '@/libs/pattern/patternEditor'
import {
  getGripperBorders,
  getMaxNumberBoxes,
  isGripperInsidePallet, isInLine3,
  isQuadPick, isRI20,
} from '@/libs/pattern/gripperUtils'
import BoxOrientationOnConveyorEnum = ProgramSettings.BoxOrientationOnConveyorEnum
import { defaultPusherMaxBoxWidth } from '@/api/const'

export interface Block extends BoxPosition {
  boxes: BoxPosition[]
  length: number
  width: number
  top: number
  left: number
  right: number
  bottom: number
}

// 2, 3, 4 boxes in groups ...
// never change box id !
export const makeBlocks = (positions: BoxPosition[], template: Box, groups: number[][]) => {
  const blocks: Block[] = []
  const ignore: number[] = []

  for (const g of groups) {
    let accX: number = 0
    let accY: number = 0
    let top: number = 100000
    let bottom: number = 0
    let left: number = 100000
    let right: number = 0
    let groupLength = 0
    let block: Block = {boxes: [], length: 0, width: 0, rotation: 0, top: 0, left: 0, right: 0, bottom: 0}

    for (const id of g) {
      const box = positions.find (b => b.id === id)
      if (box) {
        ignore.push (id)
        groupLength += 1 // ordering
        accX += box.x!
        accY += box.y!
        block.boxes.push (box)

        const boxTop = topSide (box, template)
        top = boxTop < top ? boxTop : top

        const boxBottom = bottomSide (box, template)
        bottom = boxBottom > bottom ? boxBottom : bottom

        const boxLeft = leftSide (box, template)
        left = boxLeft < left ? boxLeft : left

        const boxRight = rightSide (box, template)
        right = boxRight > right ? boxRight : right
      }
    }
    if (groupLength > 0) {
      block.x = Math.floor (left + (right - left) / 2)
      block.y = Math.floor (top + (bottom - top) / 2)
      block.length = right - left
      block.width = bottom - top
      block = {...block, top, left, right, bottom}
    }
    block.boxes.sort (byRotationAndPosition)
    // console.log(`makeBlocks - block [${block.boxes[0].id}, ${block.boxes[1].id}] x: ${block.x}, y: ${block.y}, length: ${block.length}, width: ${block.width} `)
    blocks.push (block)
  }

  // for boxes not included in groups
  positions.filter (q => !ignore.includes (q.id!)).forEach (p => {
    const block: Block = {boxes: [], length: 0, width: 0, rotation: 0, top: 0, left: 0, right: 0, bottom: 0}
    block.boxes.push (p)
    block.x = p.x
    block.y = p.y
    // block.rotation = p.rotation!

    block.length = Math.round (template.length! * Math.cos (p.rotation! / 180 * Math.PI)
      + template.width! * Math.sin (p.rotation! / 180 * Math.PI))

    block.width = Math.round (template.width! * Math.sin (p.rotation! / 180 * Math.PI)
      + template.length! * Math.cos (p.rotation! / 180 * Math.PI))

    block.top = topSide (p, template)
    block.left = leftSide (p, template)
    block.right = rightSide (p, template)
    block.bottom = bottomSide (p, template)
    blocks.push (block)
  })

  return blocks
}

export const byRotationAndPosition = (a: BoxPosition, b: BoxPosition): number => {
  if (a.rotation === b.rotation) {
    if (a.rotation === 0 && a.y === b.y) {
      return a.x! > b.x! ? 1 : -1
    }
    if (a.rotation === 180 && a.y === b.y) {
      return a.x! < b.x! ? 1 : -1
    }
    if (a.rotation === 90 && a.x === b.x) {
      return a.y! < b.y! ? 1 : -1
    }
    if (a.rotation === 270 && a.x === b.x) {
      return a.y! > b.y! ? 1 : -1
    }
  }
  return -1
}

export const checkSinglePlaceOk = (group: BoxPosition[], template: Box, settings: MachineData): {ok: boolean, group: BoxPosition[], aligned: boolean, withPusher?: boolean} => {
  if (isInLine3 (settings.robotType, settings.gripperSections)) {
    return checkSinglePlaceInLine (group, template, settings)
  }
  if (isQuadPick (settings)) {
    // 2 with/without pusher or 4 boxes with pusher
    return checkSinglePlace4Section (group, template, settings)
  }
  return checkSinglePlace2Section (group, template, settings)
}

const checkSinglePlace2Section = (group: BoxPosition[], template: Box, settings: MachineData): {ok: boolean, group: BoxPosition[], aligned: boolean, withPusher?: boolean} => {
  console.log ('layerSort.checkSinglePlace2Section', JSON.stringify (group))
  if (group.length !== 2) {
    console.log ('[ERROR] groups work only for 2 boxes')
    return {ok: false, group: [], aligned: false}
  }
  const box1 = group[0]
  const box2 = group[1]
  if (!box1 || !box2) {
    return {ok: false, group: [], aligned: false}
  }

  const verticalAligned = equal (box1.x!, box2.x!)
  const horizontalAligned = equal (box1.y!, box2.y!)

  const verticalDistance = Math.abs (box1.y! - box2.y!)
  const horizontalDistance = Math.abs (box1.x! - box2.x!)

  const angle = box1.rotation! % 360
  const w = Math.abs (
    template.length! * Math.round (Math.cos (angle / 180 * Math.PI)) +
    template.width! * Math.round (Math.sin (angle / 180 * Math.PI))
  )
  const h = Math.abs (
    template.width! * Math.round (Math.cos (angle / 180 * Math.PI)) +
    template.length! * Math.round (Math.sin (angle / 180 * Math.PI))
  )
  console.log ('w, h, angle, template', w, h, angle, JSON.stringify (template))

  const isSSL = settings.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING'
  const distanceOnConveyor = isSSL ? template.length! : template.width!
  const verDistanceOk = isSSL ? isAligned (verticalDistance, h) : isAligned (verticalDistance, w)
  const horDistanceOk = isSSL ? isAligned (horizontalDistance, w) : isAligned (horizontalDistance, h)

  // check minimal distance: vertical or horizontal
  if (verticalAligned && !(verDistanceOk && isAligned (distanceOnConveyor, verticalDistance))) {
    console.log ('[DEBUG] Distance not matching (1)', {verticalDistance, h, distanceOnConveyor})
    return {ok: false, group: [], aligned: false}
  }
  if (horizontalAligned && !(horDistanceOk && isAligned (distanceOnConveyor, horizontalDistance))) {
    console.log ('[DEBUG] Distance not matching (2)', {horizontalDistance, w, distanceOnConveyor})
    return {ok: false, group: [], aligned: false}
  }

  if (!verticalAligned && !horizontalAligned) {
    console.log ('[ERROR] grouping failed, boxes are not aligned')
    return {ok: false, group: [], aligned: false}
  }

  // check if the boxes have the same rotation
  if (box1.rotation! % 360 !== box2.rotation! % 360) {
    console.log ('[ERROR] grouping failed, angles not matching (1)')
    return {ok: false, group: [], aligned: verticalAligned || horizontalAligned}
  }

  // order inside group
  const result = [...group].sort (alpha4Func)

  return {ok: true, group: result, aligned: true}
}

const checkSinglePlaceInLine = (group: BoxPosition[], template: Box, settings: MachineData): {ok: boolean, group: BoxPosition[], aligned: boolean} => {
  console.log ('layerSort.checkSinglePlaceInLine', JSON.stringify (group))
  const numBoxes = group.length
  if (!getMaxNumberBoxes(settings).includes(numBoxes)) {
    return {ok: false, group: [], aligned: false}
  }
  const alignedBoxes = getAlignedGroupsInline(group, template.length!, template.width!, settings.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING')
  if (alignedBoxes.vertical.length !== group.length && alignedBoxes.horizontal.length !== group.length) {
    console.log ('[ERROR] single place not possible, boxes are not aligned')
    return {ok: false, group: [], aligned: false}
  }
  const aligned = (alignedBoxes.vertical.length > 1) ? alignedBoxes.vertical : alignedBoxes.horizontal
  const ordered = [...aligned].sort(alpha4Func)
  return {ok: true, group: ordered, aligned: true}
}

const checkSinglePlace4Section = (group: BoxPosition[], template: Box, settings: MachineData): {ok: boolean, group: BoxPosition[], aligned: boolean, withPusher: boolean} => {
  console.log ('layerSort.checkSinglePlace4Section', JSON.stringify (group))
  const numBoxes = group.length
  if (!getMaxNumberBoxes(settings).includes(numBoxes)) {
    // console.log ('[ERROR] groups work only for 2 or 4 boxes')
    return {ok: false, group: [], aligned: false, withPusher: false}
  }
  if (group.filter (g => g).length !== numBoxes) {
    return {ok: false, group: [], aligned: false, withPusher: false}
  }

  const box1 = group[0]
  const angle1 = box1.rotation! % 360
  // check if the boxes have the same rotation
  if (group.filter (b => b.rotation! % 360 !== angle1).length > 0) {
    console.log ('[ERROR] grouping failed, angles not matching (1)')
    return {ok: false, group: [], aligned: false, withPusher: false}
  }

  const w = Math.abs (
    template.length! * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
    template.width! * Math.round (Math.sin (angle1 / 180 * Math.PI))
  )
  const h = Math.abs (
    template.width! * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
    template.length! * Math.round (Math.sin (angle1 / 180 * Math.PI))
  )
  console.log ('w, h, angle, template', w, h, angle1, JSON.stringify (template))
  const testBoxes = getTestBoxes (group, w, h, settings.boxOrientationOnConveyor === BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING)

  // alignment condition for 2 boxes
  const is2BoxesVerticalAligned = testBoxes.filter (tb => tb.verticalAligned > 0 && tb.bp.rotation! % 180 === 0 && tb.verticalDistanceOK).length === 2
    || testBoxes.filter (tb => tb.horizontalAligned > 0 && tb.bp.rotation! % 180 === 90 && tb.horizontalDistanceOK).length === 2
  // console.log('is2BoxesVerticalAligned: ', is2BoxesVerticalAligned)
  const is2BoxesHorizontalAligned = testBoxes.filter (tb => tb.horizontalAligned > 0 && tb.bp.rotation! % 180 === 0 && tb.horizontalDistanceOK).length === 2
    || testBoxes.filter (tb => tb.verticalAligned > 0 && tb.bp.rotation! % 180 === 90 && tb.verticalDistanceOK).length === 2
  // console.log('is2BoxesHorizontalAligned: ', is2BoxesHorizontalAligned)
  if ((numBoxes === 4 && testBoxes.some (tb => (tb.verticalAligned < 0 || !tb.verticalDistanceOK) || (tb.horizontalAligned < 0 || !tb.horizontalDistanceOK)))
    || (numBoxes === 2 && !is2BoxesHorizontalAligned && !is2BoxesVerticalAligned)) {
    console.log ('[ERROR] grouping failed, boxes are not aligned (1)')
    return {ok: false, group: [], aligned: false, withPusher: false}
  }

  const pusherPossible = pusherPrepPossible(template, settings)
  if ((is2BoxesVerticalAligned || testBoxes.filter (tb => tb.verticalAligned > 0 && tb.horizontalAligned > 0).length === 4) && !pusherPossible) {
    console.log ('[DEBUG] cannot group with pusher - box too wide')
    return {ok: false, group: [], aligned: true, withPusher: false}
  }

  const usePusher = (numBoxes === 4) ? true : is2BoxesVerticalAligned
  if (usePusher && !settings.pusher) {
    return {ok: false, group: [], aligned: true, withPusher: false}
  }

  const result = [...group].sort (alpha5Func)

  return {ok: true, group: result, aligned: true, withPusher: usePusher}
}

const equal = (a: number, b: number): boolean => {
  return a === b
}

const isAligned = (a: number, b: number): boolean => {
  return Math.abs(a - b) <= SIDE_MARGIN
}

interface TestBox {
  bp: BoxPosition,
  horizontalAligned: number,
  verticalAligned: number,
  horizontalDistance: number,
  horizontalDistanceOK: boolean,
  verticalDistance: number,
  verticalDistanceOK: boolean
}

const getTestBoxes = (group: BoxPosition[], w: number, h: number, ssl: boolean): TestBox[] => {
  const testBoxes: TestBox[] = []
  for (const b1 of group) {
    const box = {
      bp: b1,
      verticalAligned: -1,
      horizontalAligned: -1,
      verticalDistance: 0,
      horizontalDistance: 0,
      horizontalDistanceOK: false,
      verticalDistanceOK: false
    }
    for (const b2 of group) {
      if (b1.id === b2.id) {
        continue
      }
      // console.log(`check boxes: ${b1.id}, ${b2.id}, x [${b1.x},${b2.x}], y [${b1.y},${b2.y}]`)
      if (equal (b1.x!, b2.x!)) {
        box.verticalAligned = b2.id!
        box.verticalDistance = Math.abs (b1.y! - b2.y!)
        // console.log('verticalDistance: ', box.verticalDistance, h, b1.id, b2.id, ssl)
        box.verticalDistanceOK = ssl ? equal (box.verticalDistance, h) : equal (box.verticalDistance, w)
      }
      if (equal (b1.y!, b2.y!)) {
        box.horizontalAligned = b2.id!
        box.horizontalDistance = Math.abs (b1.x! - b2.x!)
        // console.log('horizontalDistance: ', box.horizontalDistance, w, b1.id, b2.id, ssl)
        box.horizontalDistanceOK = ssl ? equal (box.horizontalDistance, w) : equal (box.horizontalDistance, h)
      }
    }
    testBoxes.push (box)
  }
  // console.log('testBoxes: ', JSON.stringify(testBoxes))
  return testBoxes
}

export const renumberBoxes = (positions: BoxPosition[],
                              template: Box,
                              isLeft: boolean = true,
                              groups: number[][],
                              boxOrientationOnConveyor: ProgramSettings.BoxOrientationOnConveyorEnum = ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING,
): BoxPosition[] => {

  // console.log('[DEBUG] PALLET SIDE:', (isLeft ? 'LEFT' : 'RIGHT'))
  // console.log('[DEBUG] renumberBoxes, groups', JSON.stringify(groups))
  // console.log('[DEBUG] positions', JSON.stringify(positions))
  // console.log('[DEBUG] positions', JSON.stringify(positions.map(p => p.id)))
  let collection: BoxPosition[] = isLeft ? [...positions] : alternate ([...positions])

  // if long side leading, swap width and length of box
  if (boxOrientationOnConveyor === ProgramSettings.BoxOrientationOnConveyorEnum.LONG_SIDE_LEADING) {
    template = Object.assign ({}, template)
    const oldWidth = template.width
    template.width = template.length
    template.length = oldWidth
    const oldWidthMargin = template.widthMargin
    template.widthMargin = template.lengthMargin
    template.lengthMargin = oldWidthMargin
  }

  const boxDef = new BoxDef ()
  boxDef.width = template.length!
  boxDef.height = template.width!
  boxDef.actualHeight = template.height!
  boxDef.widthMargin = template.lengthMargin!
  boxDef.heightMargin = template.widthMargin!

  // make blocks only from 'all place groups'
  // groups with boxes placed separately are not included
  // @input positions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], groups: [[4, 5], [8, 9]]
  // @output blocks: [[1], [2], [3], [4, 5], [6], [7], [8, 9], 10]
  const blocks = makeBlocks (collection, template, [...groups])
  // console.log ('[DEBUG] makeBlocks', JSON.stringify (blocks.map (p => p.boxes.map (b => b.id))))
  // blocks.forEach(b => {
  //  console.log('[DEBUG] block', JSON.stringify(b))
  // })

  // @input blocks: [[1], [2], [3], [4, 5], [6], [7], [8, 9], [10]]
  // @output blocks: [[1], [2], [4, 5], [3], [6], [7], [8, 9], [10]]
  const sortedBlocks = sortBlocks (blocks, boxDef)
  // console.log ('[DEBUG] sorted blocks', JSON.stringify (sortedBlocks.map (p => p.boxes.map (b => b.id))))

  // sort boxes inside blocks, but not sort blocks
  // @input blocks: [[1], [2], [4, 5], [3], [6], [7], [8], [9], [10]]
  // @output collection [1, 2, 5, 4, 3, 6, 7, 8, 9, 10]
  // collection = setBoxNumbers(sortedBlocks)
  collection = []
  for (const block of sortedBlocks) {
    collection.push (...block.boxes)
  }

  // revert coordinates
  if (!isLeft) {
    collection = alternate (collection)
  }
  // console.log('[DEBUG] sorted', JSON.stringify(collection))
  // console.log ('Left: ' + isLeft + ', ' + collection.map (p => p.id).join (','))
  return collection
}

/**
 * Sort blocks of boxes by placing order.
 * If only one box in a block, it will be the same order on conveyor for picking as well.
 */
export const sortBlocks = (blocks: Block[], boxDef: BoxDef) => {
  // console.log('[DEBUG] sortBlocks - before', JSON.stringify(blocks.map(p => p.boxes.map(b => b.id))))
  const collection = [...blocks]
  const sorted = collection.sort (byArea)
  // console.log('[DEBUG] sorted blocks 0.1', JSON.stringify(sorted.map(p => p.boxes.map(b => b.id))))
  // 2) pop-up boxes that have dependencies
  popupDependentBoxes (sorted, boxDef)
  // for (const s of sorted) {
  //   console.log(`[DEBUG] sorted`, s.x, s.y!, s.boxes.map(p => p.id).join(','))
  // }
  // console.log('[DEBUG] sortBlocks - after', JSON.stringify(sorted.map(p => p.boxes.map(b => b.id))))
  return sorted
}

// export const getPlaceOrder = (boxes: BoxPosition[], isLeft: boolean): BoxPosition[] => {
//   const alpha5 = (p: BoxPosition) => {
//     if (isLeft) {
//       return 2 * p.x! + 4 * p.y!
//     }
//     return 2 * p.x! - 4 * p.y!
//   }
//   const byAlpha5 = (a: BoxPosition, b: BoxPosition) => {
//     return alpha5(a) < alpha5(b) ? -1 : 1
//   }
//   return [...boxes].sort(byAlpha5)
// }

const alpha4 = (p: BoxPosition) => {
  // console.log('fn: alpha4')
  return Math.floor (
    2 * (p.x! * Math.cos (p.rotation! / 180 * Math.PI) - p.y! * Math.sin (p.rotation! / 180 * Math.PI)) +
    1 * (-p.x! * Math.sin (p.rotation! / 180 * Math.PI) + p.y! * Math.cos (p.rotation! / 180 * Math.PI))
  )
}

export const alpha4Func = (a: BoxPosition, b: BoxPosition) => {
  return alpha4 (a) < alpha4 (b) ? -1 : 1
}

// fix order inside a group with pusher
export const alpha5 = (p: BoxPosition) => {
  // console.log('fn: alpha5')
  return Math.floor (
    1 * (-p.x! * Math.cos (p.rotation! / 180 * Math.PI) - p.y! * Math.sin (p.rotation! / 180 * Math.PI)) +
    3 * (-p.x! * Math.sin (p.rotation! / 180 * Math.PI) + p.y! * Math.cos (p.rotation! / 180 * Math.PI))
  )
}

export const alpha5Func = (a: BoxPosition, b: BoxPosition) => {
  if (a.rotation === 90 || a.rotation === 270) {
    return alpha5 (a) < alpha5 (b) ? -1 : 1
  }
  return alpha5 (a) > alpha5 (b) ? -1 : 1
}

// Make sorted array of box positions
// export const setBoxNumbers = (blocks: Block[]): BoxPosition[] => {
//   // alpha is an 'weight value' that shows box ordering in the group
//   //   rotation (R) influence to ordering
//   //   x, y - box position center point coordinates
//   //   alpha = x * cos(R) + y * sin(R)
//   //   If rotation is 0 or 180, boxes are placed on the pallet horizontally and ordering depends on x value,
//   //   if rotation is 90 or 270, boxes are placed on the pallet vertically and ordering depends on y value
//   //   Gripper should pickup boxes this way, number is box ordering on conveyor as they come:
//   //     [ 1 ][ 2 ]        [ 1 ][ 2 ][ 3 ]      [ 1 ][ 2 ][ 3 ][ 4 ]
//   //
//   // alpha4 is an extended 'weight value' that works for 2-dimensional group for boxes, like 2x2, 3x2, 3x3 etc.
//   //   Gripper should pickup boxes this way, number is box ordering on conveyor as they come:
//   //     [ 1 ][ 2 ]        [ 1 ][ 2 ][ 3 ]
//   //     [ 3 ][ 4 ]        [ 4 ][ 5 ][ 6 ]
//   //
//   const alpha = (p: BoxPosition) => {
//     console.log('fn: alpha')
//     Math.floor(p.x! * Math.cos(p.rotation! / 180 * Math.PI) - p.y! * Math.sin(p.rotation! / 180 * Math.PI))
//   }
//
//   // 2 * (x * cos(R) - y * sin(R)) + (-x * sin(R) + y * cos(R))
//   // For 0, 180  boxes we have 2x + y
//   // For 90, 270 boxes we have -2y - x
//   //
//   // example:
//   //   box1 [200, 200, 0]; alpha4 = 2*200 + 200 = 600 -> #1
//   //   box2 [500, 200, 0]; alpha4 = 2*500 + 200 = 1200 -> #2
//   //
//   //   box1 [200, 200, 0]; alpha4 = 2*200 + 200 = 600 -> #1
//   //   box2 [200, 300, 0]; alpha4 = 2*200 + 300 = 700 -> #2
//   //
//   //   box1 [200, 200, 0]; alpha4 = 2*200 + 200 = 600 -> #1
//   //   box2 [300, 300, 0]; alpha4 = 2*300 + 300 = 900 -> #2
//   //
//   //   box1 [200, 200, 90]; alpha4 = -2*200 - 200 = -600 -> #2
//   //   box2 [500, 200, 90]; alpha4 = -2*200 - 500 = -1200 -> #1  FAIL
//   //
//   //
//
//   // 2 * (x * cos(R) - y * sin(R)) + (x * sin(R) - y * cos(R))
//   // For 0, 180  boxes we have 2x - y
//   // For 90, 270 boxes we have -2y + x
//   //
//   // example:
//   //   box1 [200, 200, 0]; alpha4 = 2*200 - 200 = 200 -> #1
//   //   box2 [500, 200, 0]; alpha4 = 2*500 - 200 = 800 -> #2
//   //
//   //   box1 [200, 200, 0]; alpha4 = 2*200 - 200 = 200 -> #2
//   //   box2 [200, 300, 0]; alpha4 = 2*200 - 300 = 100 -> #1
//   //
//   //   box1 [200, 200, 0]; alpha4 = 2*200 + 200 = 600 -> #1
//   //   box2 [300, 300, 0]; alpha4 = 2*300 + 300 = 900 -> #2
//   //
//   //   box1 [200, 200, 90]; alpha4 = -2*200 + 200 = -200 -> #1
//   //   box2 [500, 200, 90]; alpha4 = -2*200 + 500 = 100 -> #2
//   //
//   //
//
//
//   // 2-boxes sort for single placed boxes in a group, when boxes same or opposite direction
//   const alpha2 = (p: BoxPosition) => {
//     console.log('fn: alpha2')
//     return -(Math.floor(p.x! * Math.cos(p.rotation! / 180 * Math.PI)
//       - p.y! * Math.sin(p.rotation! / 180 * Math.PI)))
//   }
//
//   // 2-boxes sort for single placed boxes in a group, when boxes have not same or opposite direction
//   const alpha2_90 = (p: BoxPosition) => {
//     console.log('alpha2_90')
//     const tailFactor = p.rotation! === 90 || p.rotation! === 180 ? 1 : 0
//     const bigN = 99999
//
//     return Math.floor(p.x! * Math.cos(p.rotation! / 180 * Math.PI) - p.y! * Math.sin(p.rotation! / 180 * Math.PI))
//       + bigN * tailFactor
//   }
//
//   /*  a = 1, b = 2
//   cx1, cy1
//   cx2, cy2
//   vector [[cx1, cxy1], [cx2, cy2]]
//
//   // l1: y = m1x + c1
//   // l2: y = m2x + c2
//   // If the point (x0,y0) lies below l1 and above l2, then m1x0+c1>y0>m2x0+c2
// .
//   // find a line:
//   // {x1, y1} {x2, y2} -> {m1, c1}
//   // y1 = m1x1 + c1
//   // y2 = m1x2 + c1
//   // y2 - y1 = m1x2 - m1x1
//   // -----------------------------
//   // m1 = (y2 - y1) / (x2 - x1)
//   // c1 = y1 - m1x1
//   // -----------------------------
//
//   //
//
//
//
//   */
//
//   /*
//   id: 3, x: 106, y: 726, rotation: 0
//   alpha2_90 -> 106 * 1 - 0 + 99999 * 0 -> 106
//
//   id: 2, x: 73, y: 548, rotation: 270
//   alpha2_90 -> 0 - 548 * (sin(270/180*pi)) + 0 -> -548 * -1 -> 548
//
//   106 < 548 -> [id: 3, id: 2]
//
//    */
//
//   let fn = (b: BoxPosition): number => {
//     return 0
//   }
//
//   const byAlpha = (a: BoxPosition, b: BoxPosition) => {
//     if (a.rotation === b.rotation) {
//       fn = alpha4
//       return alpha4(a) < alpha4(b) ? -1 : 1
//     }
//     if (Math.abs(a.rotation! - b.rotation!) % 180 === 0) {
//       fn = alpha2
//       return alpha2(a) < alpha2(b) ? -1 : 1
//     }
//     if (Math.abs(a.rotation! - b.rotation!) % 180 === 90) {
//       fn = alpha2_90
//       return alpha2_90(a) < alpha2_90(b) ? -1 : 1
//     }
//     console.error('[ERROR] wrong sort in group getBoxNumbers', JSON.stringify(a), JSON.stringify(b))
//     return -1
//   }
//   console.log('[DEBUG] before alpha', JSON.stringify(blocks))
//   const result: BoxPosition[] = []
//   for (const block of blocks) {
//     const blockBoxes = [...block.boxes]
//
//
//     // Sort boxes in the group, depends on rotation and label position
//     // ... the first box in the group should be the first box on conveyor
//     if (blockBoxes.length > 1) {
//       //// const placeAllTogether = checkPlaceAllOk(blockBoxes, template)
//       // console.log('placeAllTogether', JSON.stringify(blockBoxes))
//       //// if (placeAllTogether) {
//       // blockBoxes.sort(byAlpha)
//       //// }
//     }
//
//     if (blockBoxes.length === 2) {
//       console.log('alpha', fn(blockBoxes[0]), fn(blockBoxes[1]))
//     }
//     result.push(...blockBoxes)
//   }
//   console.log('[DEBUG] after alpha', JSON.stringify(result))
//   return result
// }

const popupDependentBoxes = (sorted: Block[], boxDef: BoxDef) => {
  for (let i = 1; i < sorted.length; i++) {
    // console.log(`[DEBUG] try actor at [${i}] ids=${sorted[i].boxes.map(p => p.id).join(',')}`)
    putBeforeFirstDependentBlock (i, sorted, boxDef)
  }
}
// index is zero-based number of an element in the 'positions' list
const putBeforeFirstDependentBlock = (index: number, blocks: Block[], boxDef: BoxDef) => {
  // console.log(`[DEBUG] putBeforeFirstDependentBlock - index ${index}, blocks ${blocks.map(b => b.boxes.map(p => p.id).join(','))}`)
  const actor = blocks[index]
  for (let i = 0; i < index; i++) {
    const passive = blocks[i]
    const dep = isDependedBlock2 (passive, actor, boxDef)
    // console.log(`[DEBUG] putBeforeFirstDependentBlock - actor ${JSON.stringify(actor.boxes.map(p => p.id))}, passive ${JSON.stringify(passive.boxes.map(p => p.id))} - before: ${dep}, ${i}`)
    if (dep) {
      // insert actor element before i position
      blocks.splice (index, 1)
      blocks.splice (i, 0, actor)

      const depReversed = isDependedBlock2 (actor, passive, boxDef)
      // console.log(`[DEBUG] putBeforeFirstDependentBlock - depReversed: `, depReversed, JSON.stringify(blocks.map(b => b.boxes.map(p => p.id).join(','))))
      if (!depReversed) {
        popupDependentBoxes (blocks, boxDef)
      }
      return
    }
  }
}

const isDependedBlock2 = (passive: Block, actor: Block, boxDef: BoxDef): boolean => {
  const isDependent = []
  // console.log (`isDependedBlock2 - actor: ${ JSON.stringify (actor.boxes.map(p => p.id).join(',')) }`)
  // console.log (`isDependedBlock2 - passive: ${ JSON.stringify (passive.boxes.map(p => p.id).join(',')) }`)
  const max2Boxes = (actor.boxes.length + passive.boxes.length) === 2

  for (const ab of actor.boxes) {
    const isDependentBlock = []
    for (const pb of passive.boxes) {
      const depCheck = checkDependencyOrder (pb, ab, boxDef, 'L') // [!] always 'L' for correct block sort, need to refactor it
      const depCheckOk = depCheck.ok && depCheck.ordered[0].id === ab.id
      // console.log (`isDependedBlock2: ${ ab.id }, ${ pb.id } - ok: ${ depCheck.ok }, depCheckOk: ${ depCheckOk }, distanceOk: ${depCheck.distanceOk}`)
      if (max2Boxes && depCheckOk) {
        return true
      }
      // actor cannot be placed before
      if (depCheck.ok && depCheck.ordered[1].id === ab.id && !depCheck.distanceOk) {
        return false
      }
      // only register results with order dependency
      if (depCheck.ok) {
        isDependentBlock.push (depCheckOk)
      }
      // console.log (`isDependedBlock2 - ${ ab.id }, ${ pb.id } - isDependentBlock: ${ JSON.stringify(isDependentBlock) }`)
    }
    // skip empty results
    if (isDependentBlock.length > 0) {
      const dep = isDependentBlock.some(d => d)
      isDependent.push (dep)
    }
  }
  // place before if all boxes with dependent order should be placed before
  const anyDependent = isDependent.length > 0 && isDependent.every(d => d)
  // console.log (`isDependedBlock2 - isDependent: ${ JSON.stringify (isDependent) }, anyDependent: ${ anyDependent } [${actor.boxes.length}, ${passive.boxes.length}]`)
  return anyDependent
}

// const isDependedBlock = (passive: Block, actor: Block): boolean => {
//   // if two boxes have horizontal intersection (there is a horizontal line that crosses both boxes)
//   // and 'actor' is to the left from 'passive'
//   const intersection = Math.floor(topSide(actor, actor)) < Math.floor(bottomSide(passive, passive))
//     && Math.floor(bottomSide(actor, actor)) + 1 > Math.floor(topSide(passive, passive))
//
//   const actorIsBefore = Math.floor(actor.x!) < Math.floor(passive.x!)
//
//   // if two boxes have vertical intersection (there is a vertical line that crosses both boxes)
//   // and 'actor' is higher then 'passive'
//   // const verticalIntersection = Math.floor(leftSide(actor, actor)) < Math.floor(rightSide(passive, passive))
//   //   && Math.floor(rightSide(actor, actor)) > Math.floor(leftSide(passive, passive))
//   // const actorIsHigher = Math.floor(actor.y!) < Math.floor(passive.y!)
//
//   const horizontalDependency = intersection && actorIsBefore
//   // const verticalDependency = verticalIntersection && actorIsHigher
//
//   // const actorIds = actor.boxes.map(p => p.id).join(',')
//   // const passiveIds = passive.boxes.map(p => p.id).join(',')
//   // if (actor.boxes.some(p => p.id === 19 || p.id === 6)) {
//   //  console.log(`[DEBUG] ${actorIds} dependencies`, passiveIds, { horizontalDependency, verticalDependency })
//   // console.log({actor_left, actor_right, passive_left, passive_right})
//   // console.log({verticalIntersection, actorIsHigher})
//   // console.log({actor_Y: actor.y!, passive_Y: passive.y!, actorBottomSide: bottomSide(actor, actor), passiveTopSide: topSide(passive, passive)})
//   // }
//
//   return horizontalDependency // || verticalDependency
// }

export const alternate = (array: BoxPosition[]) => {
  const big = 10000
  const result: BoxPosition[] = []
  array.forEach (b => {
    // 'y' value changes for sorting and then changes back, finally the same as originally
    const nb: BoxPosition = {id: b.id, x: b.x, y: (-b.y! + big), rotation: b.rotation, lpbuId: b.lpbuId }
    flipVerticalBox (nb)
    result.push (nb)
  })
  return result
}

const flipVerticalBox = (p: BoxPosition) => {
  if (p.rotation === 90) {
    p.rotation = 270
    return
  }
  if (p.rotation === 270) {
    p.rotation = 90
  }
}

// export const sortBoxes = (positions: BoxPosition[], template: Box): BoxPosition[] => {
//   // 1) sort by x, y
//   const collection = [...positions]
//   const sorted = collection.sort(byCenterCoordinate)
//
//   // 2) pop-up boxes that have dependencies
//   for (let i = 1; i < sorted.length; i++) {
//     // console.log(`[DEBUG] try actor at [${i}] id=${sorted[i].id}`)
//     putBeforeFirstDependent(i, sorted, template)
//   }
//   return sorted
// }

// const byCenterCoordinate = (a: BoxPosition, b: BoxPosition): number => {
//   return a.y! < b.y! ? -1 :
//     (a.y! > b.y! ? 1 :
//         (a.x! < b.x! ? -1 : 1)
//     )
// }

// const byAreaPosition = (boxDef: BoxDef): any => {
//   return (a: BoxPosition, b: BoxPosition): number => {
//     const inOneRow = topSide2(a, boxDef) < bottomSide2(b, boxDef) && topSide2(b, boxDef) < bottomSide2(a, boxDef)
//     if (inOneRow) {
//       return a.x! < b.y! ? -1 : 1
//     }
//     const inOneColumn = leftSide2(a, boxDef) < rightSide2(b, boxDef) && leftSide2(b, boxDef) < rightSide2(a, boxDef)
//     if (inOneColumn) {
//       return a.y! < b.y! ? -1 : 1
//     }
//     return a.x! < b.y! ? -1 : (a.y! < b.y! ? -1 : 1)
//   }
// }

export const byArea = (a: Block, b: Block): number => {
  const inOneRow = a.top < b.bottom && b.top < a.bottom
  const inOneColumn = a.left < b.right && b.left < a.right
  const yIsLower = a.y! < b.y!
  const xIsLower = a.x! < b.x!
  // console.log('byArea - a, b:', JSON.stringify(a.boxes.map(box => box.id)), JSON.stringify(b.boxes.map(box => box.id)))
  // console.log(`byArea - inOneRow: ${inOneRow}, inOneColumn: ${inOneColumn}`)
  // console.log(`byArea - xIsLower: ${xIsLower} (${a.x}, ${b.x}), yIsLower: ${yIsLower} (${a.y}, ${b.y})`)
  if (inOneRow && !inOneColumn) {
    return xIsLower ? -1 : 1
  }
  if (inOneColumn && !inOneRow) {
    return yIsLower ? -1 : 1
  }
  return yIsLower ? -1 : 1
}

// index is zero-based number of an element in the 'positions' list
const putBeforeFirstDependent = (index: number, positions: BoxPosition[], template: Box) => {
  for (let i = 0; i < index; i++) {
    const actor = positions[index]
    const passive = positions[i]
    const dep = isDepended (passive, actor, template, template)
    // console.log(`[DEBUG] isDependent ${passive.id} on ${actor.id} = ${dep}`)
    if (dep) {
      // insert actor element before i position
      positions.splice (index, 1)
      positions.splice (i, 0, actor)
      return
    }
  }
}

const isDepended = (passive: BoxPosition, actor: BoxPosition, templateP: Box, templateA: Box): boolean => {
  // if two boxes have horizontal interception (there is a horizontal line that crosses both boxes)
  // and 'actor' is to the left from 'passive'
  const interception = topSide (actor, templateA) < bottomSide (passive, templateP)
    && bottomSide (actor, templateA) > topSide (passive, templateP)
  const actorIsBefore = actor.x! < passive.x!
  return interception && actorIsBefore
}

export const getRobotMovementNumber = (id: number, positions: BoxPosition[], groups: number[][]): [number, number] => {
  let groupFoundIndex = -1
  let processedInGroups: number[] = []
  let num = 0
  for (const pos of positions) {
    groupFoundIndex = -1
    const key = pos.id || 0
    // @ts-ignore
    if (!processedInGroups.includes (key)) {
      num += 1
      groupFoundIndex = groups.findIndex (g => g.includes (key))
      if (groupFoundIndex > -1) {
        const groupFound = groups[groupFoundIndex]
        if (groupFound.includes (id)) {
          break
        }
        processedInGroups = [...processedInGroups, ...groupFound]
      }
    }
    if (key === id) {
      break
    }
  }
  // console.log('getRobotMovement', id, num, groupFoundIndex)
  return [num, groupFoundIndex]
}

// Get possible gripper sections configuration,
// depends on box position/rotation AND gripper is not outside pallet
export const checkGripperSections = (groupPlaces: BoxPosition[], otherGroup: BoxPosition[], positions: BoxPosition[], sections: number[],
                                     boxDef: BoxDef, palDef: PalletDef, ignoreGripperOutside: boolean, settings: MachineData, pusherGrouped: boolean, ignoreOverlapCheck: boolean, side: string)
  : {ok: boolean, overlapIds: number[]} => {
  if (!getMaxNumberBoxes (settings).includes (groupPlaces.length)) {
    return {ok: false, overlapIds: []}
  }

  let isInside = Boolean (ignoreGripperOutside)
  if (!ignoreGripperOutside) {
    isInside = isGripperInsidePallet (groupPlaces, sections, boxDef, palDef, settings, side)
    if (!isInside) {
      console.log ('[DEBUG] checkGripperSections - isOutside', JSON.stringify (groupPlaces.map (p => p.id)), {isInside})
      return {ok: false, overlapIds: []}
    }
  }
  if (!ignoreOverlapCheck) {
    const overlapIds = isGripperGroupOverlap(groupPlaces, otherGroup, sections, positions, boxDef, settings)
    if (overlapIds.length > 0) {
      console.log('[DEBUG] checkGripperSections - isOverlap', JSON.stringify(groupPlaces.map(p => p.id)), {
        isInside,
        overlapIds
      })
      return { ok: false, overlapIds: overlapIds }
    }
  }

  // console.log('[DEBUG] checkGripperSections', JSON.stringify(groupPlaces.map(p => p.id)), { isInside, isOverlap })
  return {ok: true, overlapIds: []} // gripper section
}

export const checkGripperSectionsWholeGroup = (groupPlaces: BoxPosition[], otherPlaces: BoxPosition[], positions: BoxPosition[], sections: number[], otherSections: number[],
                                               boxDef: BoxDef, palDef: PalletDef, ignoreGripperOutside: boolean, settings: MachineData, side: string)
  : {ok: boolean, overlapIds: number[], outsideIds: number[]} => {
  // console.log ('[DEBUG] checkGripperSectionsWholeGroup - checkGripperSectionsWholeGroup - group1 length', groupPlaces.length)
  // console.log ('[DEBUG] checkGripperSectionsWholeGroup - checkGripperSectionsWholeGroup - group2 length', [...groupPlaces, ...otherPlaces].length)
  if (!getMaxNumberBoxes (settings).includes (groupPlaces.length)
    && !getMaxNumberBoxes (settings).includes ([...groupPlaces, ...otherPlaces].length)) {
    return {ok: false, overlapIds: [], outsideIds: []}
  }

  let isInside = Boolean (ignoreGripperOutside)
  if (!ignoreGripperOutside) {
    isInside = isGripperInsidePallet (groupPlaces, sections, boxDef, palDef, settings, side)
    if (!isInside) {
      console.log ('[DEBUG] checkGripperSectionsWholeGroup - isOutside (group1)', JSON.stringify (groupPlaces.map (p => p.id)), {isInside})
      return {ok: false, overlapIds: [], outsideIds: groupPlaces.map (p => p.id!)}
    }
    isInside = isGripperInsidePallet (otherPlaces, otherSections, boxDef, palDef, settings, side)
    if (!isInside) {
      console.log ('[DEBUG] checkGripperSectionsWholeGroup - isOutside (group2)', JSON.stringify (otherPlaces.map (p => p.id)), {isInside})
      return {ok: false, overlapIds: [], outsideIds: otherPlaces.map (p => p.id!)}
    }
  }

  const overlapIds = isGripperGroupOverlapWholeGroup (groupPlaces, otherPlaces, sections, otherSections, positions, boxDef, settings, side)
  if (overlapIds.length > 0) {
    console.log ('[DEBUG] checkGripperSectionsWholeGroup - isOverlap', JSON.stringify (groupPlaces.map (p => p.id)), {
      isInside,
      overlapIds
    })
    return {ok: false, overlapIds: overlapIds, outsideIds: []}
  }
  return {ok: true, overlapIds: [], outsideIds: []} // gripper section
}

// used for SP check and DP check for DoublePick (2 section) and QuadPick (4 section) with 2 boxes
export const isGripperGroupOverlap = (group: BoxPosition[], otherGroup: BoxPosition[], sections: number[], positions: BoxPosition[], boxDef: BoxDef, settings: MachineData): number[] => {
  const place1 = group[0]
  const place2 = group[1]
  // console.log(`isGripperGroupOverlap - place1 ${JSON.stringify(place1)} - place2 ${JSON.stringify(place2)}`)
  const gripperBorders1 = getGripperBorders (place1, sections[0], boxDef, settings, true)
  const rotation = isQuadPick(settings) ? group[0].rotation! : 0
  let gripperRangesXY = getGripperRangesXY (gripperBorders1, rotation)
  if (isQuadPick(settings) && group.length === 2) {
    gripperRangesXY = getGripperRangesXYForSections(gripperBorders1, place1.rotation!, sections)
  }

  const overlapIds1 = isGripperPayloadOverlap (group, otherGroup, gripperRangesXY, positions, boxDef)
  if (overlapIds1.length > 0) {
    console.log (`[DEBUG] isGripperGroupOverlap, section [${ sections[0] }], place1:`, place1.id, 'isOverlap:', JSON.stringify (overlapIds1), JSON.stringify (gripperRangesXY))
    return [...overlapIds1, place1.id!, place2.id!]
  }
  const secondBoxRangesXY = getBoxRangesXY (place2, boxDef)
  const overlapIds2 = isGripperPayloadOverlap (group, otherGroup, secondBoxRangesXY, positions, boxDef)
  // noinspection RedundantIfStatementJS
  if (overlapIds2.length > 0) {
    console.log (`[DEBUG] isGripperGroupOverlap, section [${ sections[1] }], place2:`, place2.id, 'isOverlap:', JSON.stringify (overlapIds2), JSON.stringify (secondBoxRangesXY))
    return [...overlapIds2, place1.id!, place2.id!]
  }
  return []
}

// used for DP check QuadPick (4 section) with 4 boxes and TriplePick with 2-3 boxes
export const isGripperGroupOverlapWholeGroup = (group: BoxPosition[], otherGroup: BoxPosition[], sections: number[], otherSections: number[], positions: BoxPosition[], boxDef: BoxDef, settings: MachineData, side: string): number[] => {
  // console.log('isGripperGroupOverlapWholeGroup: ', JSON.stringify(group), JSON.stringify(otherGroup), JSON.stringify(sections), JSON.stringify(otherSections))
  const gripperBorders1 = getGripperBorders (group[0], sections[0], boxDef, settings, true)
  const rotation = isQuadPick(settings) ? group[0].rotation! : 0
  let gripperRangesXY1 = getGripperRangesXY (gripperBorders1, rotation)
  if (isInLine3(settings.robotType, settings.gripperSections) && group.length + otherGroup.length === 2) {
    gripperRangesXY1 = getGripperRangesXYTP2Boxes(gripperBorders1, group[0].rotation!)
  }
  const overlapIds = isGripperPayloadOverlap (group, otherGroup, gripperRangesXY1, positions, boxDef)
  if (overlapIds.length > 0 && !canBoxesBePlacedAfter ([...group, ...otherGroup], overlapIds, positions, boxDef, side)) {
    console.log (`[DEBUG] isGripperGroupOverlapWholeGroup (1), section [${ sections[0] }], place1:`, group[0].id, 'isOverlap:', JSON.stringify (overlapIds), JSON.stringify (gripperRangesXY1))
    return (group.length > 1) ? [...overlapIds, group[0].id!, group[1].id!] : [...overlapIds, group[0].id!]
  }
  const collisionIds = isFirstPlacementCollision(group, otherGroup, otherSections, positions, boxDef, settings, side)
  if (collisionIds.length > 0 && !canBoxesBePlacedAfter ([...group, ...otherGroup], collisionIds, positions, boxDef, side)) {
    console.log (`[DEBUG] isGripperGroupOverlapWholeGroup - collision first placement, section [${sections}], place1:`, group.map(b => b.id).join(','), 'collisions:', JSON.stringify (collisionIds))
    return (group.length > 1) ? [...collisionIds, group[0].id!, group[1].id!] : [...collisionIds, group[0].id!]
  }

  const boxRangesXY1 = getBoxRangesXY (otherGroup[0], boxDef)
  let place2BoxRangesXY = [...boxRangesXY1]
  if (otherGroup.length > 1) {
    const boxRangesXY2 = getBoxRangesXY (otherGroup[1], boxDef)
    place2BoxRangesXY = mergeRanges (boxRangesXY1, boxRangesXY2)
  }
  const isOverlapIds2 = isGripperPayloadOverlap (group, otherGroup, place2BoxRangesXY, positions, boxDef)
  if (isOverlapIds2.length > 0 && !canBoxesBePlacedAfter ([...group, ...otherGroup], isOverlapIds2, positions, boxDef, side)) {
    console.log (`[DEBUG] isGripperGroupOverlapWholeGroup (2), section [${ otherSections[0] }], place2:`, otherGroup[0].id, 'isOverlap:', JSON.stringify (isOverlapIds2), JSON.stringify (place2BoxRangesXY))
    return (group.length > 1) ? [...isOverlapIds2, group[0].id!, group[1].id!] : [...isOverlapIds2, group[0].id!]
  }
  return []
}

export const isFirstPlacementCollision = (group: BoxPosition[], otherGroup: BoxPosition[], otherSections: number[], positions: BoxPosition[], boxDef: BoxDef, settings: MachineData, side: string): number[] => {
  if (!isInLine3(settings.robotType, settings.gripperSections)
    && !(isRI20(settings.robotType) && settings.gripperSections === 2)) {
    return []
  }
  const dummyBoxes = getDummyBoxes(group, otherSections, boxDef, settings.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING')
  // get positions before each place group
  const firstIndex = positions.findIndex (p => group.map (b => b.id).includes(p.id))
  const boxesBefore = [...positions].slice (0, firstIndex).filter(p => !otherGroup.map(o => o.id).includes(p.id))
  const firstIndexOther = positions.findIndex(p => otherGroup.map(o => o.id).includes(p.id))
  const boxesBetween = [...positions].slice(firstIndex + group.length, firstIndexOther).filter(p => !group.map(o => o.id).includes(p.id))
  const allBoxesBefore = [...boxesBefore, ...boxesBetween]
  return checkPossibleCollision(allBoxesBefore, [... group, ...dummyBoxes], boxDef, side, true)
}

export const checkPossibleCollision = (boxes: BoxPosition[], actor: BoxPosition[], boxDef: BoxDef, side: string, before: boolean): number[] => {
  const collisions: number[] = []
  // console.log(`checkPossibleCollision - boxes: ${JSON.stringify(boxes.map(b => b.id))}, actors: ${JSON.stringify(actor.map(a => a.id))}`)
  for (const b of boxes) {
    for (const a of actor) {
      const movementIntersects = (before) ? isMovementIntersecting(a, b, boxDef, side) : isMovementIntersecting(b, a, boxDef, side)
      if (movementIntersects) {
        // console.log(`checkPossibleCollision - movementIntersects [${a.id}, ${b.id}]: ${movementIntersects}, (before: ${before}, side: ${side}, rotation: ${a.rotation})`)
        collisions.push(b.id!)
      }
    }
  }
  return [...new Set(collisions)]
}

export const getDummyBoxes = (group: BoxPosition[], otherSections: number[], boxDef: BoxDef, isSSL: boolean): BoxPosition[] => {
  const angle = group[0].rotation!
  // first placement > all boxes on gripper
  const dummies: BoxPosition[] = []
  const edge = otherSections[0] === 3 ? group[1] : group[0]
  // console.log(`getDummyBoxes - group: ${JSON.stringify(group)}, otherSections: ${JSON.stringify(otherSections)}, edge: ${edge.id}`)
  for (const section of otherSections) {
    let offset = isSSL ? boxDef.width : boxDef.height
    if ((section === 1 || section === 3) && otherSections.length > 1)  {
      offset = offset * 2
    }
    const before = (section === 1) || (section === 2 && otherSections[0] === 1)

    let xPos = edge.x!
    let yPos = edge.y!
    if (angle === 0) {
      xPos = (before) ? xPos - offset : xPos + offset
    } else if (angle === 90) {
      yPos = (before) ? yPos + offset : yPos - offset
    } else if (angle === 180) {
      xPos = (before) ? xPos + offset : xPos - offset
    } else if (angle === 270) {
      yPos = (before) ? yPos - offset : yPos + offset
    }
    // console.log(`getDummyBoxes - xPos: ${xPos}, yPos: ${yPos}, offset: ${offset}, edge: ${edge.id}, add before edge: ${before}`)
    dummies.push({
      id: 999 + dummies.length,
      x: xPos,
      y: yPos,
      rotation: angle
    })
  }
  // console.log(`getDummyBoxes - dummies: ${JSON.stringify(dummies)}`)
  return dummies
}

// checks if overlapping boxes can be placed after the group boxes
export const canBoxesBePlacedAfter = (group: BoxPosition[], overlapIds: number[], positions: BoxPosition[], boxDef: BoxDef, side: string = 'L'): boolean => {
  const overlappingBoxes = positions.filter(p => overlapIds.includes(p.id!))
  // console.log('canBoxesBePlacedAfter - overlappingBoxes: ', JSON.stringify(overlappingBoxes))
  const placeResults = []
  for (const box of overlappingBoxes) {
    const result = []
    const positionedBefore = []
    for (const boxOfGroup of group) {
      const depCheck = checkDependencyOrder(box, boxOfGroup, boxDef, side)
      // console.log(`canBoxesBePlacedAfter - ob: ${box.id}, gb: ${boxOfGroup.id}, depCheck: ${JSON.stringify(depCheck)}`)
      const isAfter = depCheck.ok && depCheck.ordered[0].id === boxOfGroup.id
      const isDepOK = (isAfter || depCheck.distanceOk)
      if (group.length < 2 && isDepOK) {
        return true
      }

      if (depCheck.ok) {
        // console.log(`canBoxesBePlacedAfter - isDepOK: ${isDepOK}, (isAfter: ${isAfter}, isBefore: ${isBefore}), after: ${after}`)
        result.push(isDepOK)
      }
      if (group.length > 2) {
        const isPositionedBefore = positions.findIndex(p => p.id === box.id) < positions.findIndex(p => p.id === boxOfGroup.id)
        const posChangeNOK = (!depCheck.ok && isPositionedBefore)
        if (posChangeNOK) {
          positionedBefore.push(true)
        }
      }
    }
    if (result.length > 0) {
      // console.log('canBoxesBePlacedAfter - result: ', JSON.stringify(result))
      const dep = result.every(r => r)
      placeResults.push(dep)
    } else {
      // if box already positioned before with no dependent order, it is unlikely to get moved during reordering
      const nonePositionedBefore = positionedBefore.length < 1 || positionedBefore.every(pb => !pb)
      placeResults.push(nonePositionedBefore)
    }
  }
  const placeResult = placeResults.length > 0 && placeResults.every(p => p)
  // console.log('canBoxesBePlacedAfter - placeResults: ', JSON.stringify(placeResults), placeResult)
  return placeResult
}

const mergeRanges = (ranges1: Range[], ranges2: Range[]): Range[] => {
  const xRange = [ranges1[0].a, ranges1[0].b, ranges2[0].a, ranges2[0].b]
  const yRange = [ranges1[1].a, ranges1[1].b, ranges2[1].a, ranges2[1].b]
  return [{a: Math.min (...xRange), b: Math.max (...xRange)}, {a: Math.min (...yRange), b: Math.max (...yRange)}]
}

// pRangesXY - area of gripper where we have payload (boxes, might be two or one box)
export const isGripperPayloadOverlap = (group: BoxPosition[], otherGroup: BoxPosition[], pRangesXY: Range[], positions: BoxPosition[], boxDef: BoxDef): number[] => {
  // gripper x,y ranges
  // find all group placed BEFORE group we have on gripper
  const onGripper = group.map (b => b.id)
  const firstIndex = positions.findIndex (p => onGripper.includes (p.id))
  // console.log('positions', JSON.stringify(positions.map(p => p.id)))
  // console.log('onGripper', JSON.stringify(onGripper), 'firstIndex:', firstIndex)
  const boxesBefore = [...positions].splice (0, firstIndex)
  const boxesBetween = getBoxesInBetween ([...group, ...otherGroup], positions).boxesBetween // TODO check
  const allBoxIds = [...group, ...otherGroup].map (b => b.id)
  const otherBoxes = [...boxesBefore, ...boxesBetween].filter (b => !allBoxIds.includes (b.id))
  // console.log ('[DEBUG] isGripperPayloadOverlap - otherBoxes', JSON.stringify (otherBoxes.map (b => b.id!)))
  const overlaps = otherBoxes.filter (pos => {
    const posRangesXY = getBoxRangesXY (pos, boxDef)
    // console.log(`isGripperPayLoadOverlap - (gripper x range) pRangesXY[0] ${JSON.stringify(pRangesXY[0])}, (box ${pos.id} x range) posRangesXY[0] ${JSON.stringify(posRangesXY[0])}`)
    // console.log(`isGripperPayLoadOverlap - (gripper y range) pRangesXY[1] ${JSON.stringify(pRangesXY[1])}, (box ${pos.id} y range) posRangesXY[1] ${JSON.stringify(posRangesXY[1])}`)
    const xOverLaps = isRangesOverlap (pRangesXY[0], posRangesXY[0])
    const yOverLaps = isRangesOverlap (pRangesXY[1], posRangesXY[1])
    return xOverLaps && yOverLaps
  })
  return overlaps.map(o => o.id!)
}

interface CollisionData {
  group: number[],
  placedAfterFirst: boolean,
  placedBeforeSecond: boolean
}

export const isCollisionGroup = (group: BoxPosition[], positions: BoxPosition[], groups: number[][], boxDef: BoxDef, side: string, settings: MachineData): number[] => {
  if (isQuadPick (settings) && group.length === 4) {
    return isCollisionGroup4Section (group, positions, groups, boxDef, side)
  }

  const boxesBetween = getBoxesInBetween (group, positions)
  console.log ('boxesBetween: ', JSON.stringify (boxesBetween))

  // Prepare structure for other group collision check
  const groupsCollisionData: CollisionData[] = []
  const groupMap: {[key: number]: CollisionData} = {}
  for (const g of groups) {
    const collisionData = {group: g, placedAfterFirst: false, placedBeforeSecond: false}
    groupsCollisionData.push (collisionData)
    for (const id of g) {
      groupMap[id] = collisionData
    }
  }

  const collisions = []
  for (const b of boxesBetween.boxesBetween) {
    const firstPos = boxesBetween.betweenPositions[0]
    const secondPos = boxesBetween.betweenPositions[1]
    const depCheckFirst = checkDependencyOrder (firstPos, b, boxDef, side)
    const depCheckSecond = checkDependencyOrder (b, secondPos, boxDef, side)
    const placedAfterFirst = depCheckFirst.ok && (depCheckFirst.ordered[0].id === firstPos.id) && !depCheckFirst.distanceOk
    const placedBeforeSecond = depCheckSecond.ok && (depCheckSecond.ordered[1].id === secondPos.id) && !depCheckSecond.distanceOk

    // check collisions with other groups
    const otherGroupCollisionData = groupMap[b.id!]
    if (otherGroupCollisionData != null) {
      otherGroupCollisionData.placedAfterFirst = otherGroupCollisionData.placedAfterFirst || placedAfterFirst // logic sum
      otherGroupCollisionData.placedBeforeSecond = otherGroupCollisionData.placedBeforeSecond || placedBeforeSecond // logic sum
    }
    const otherGroupCollision = groupsCollisionData.find (d => d.placedAfterFirst && d.placedBeforeSecond)
    console.log ('[DEBUG] box:', b.id, {placedAfterFirst, placedBeforeSecond})
    // console.log ('[DEBUG]', JSON.stringify ({depCheckFirst, depCheckSecond}))
    // console.log ('[DEBUG] otherGroupCollision', otherGroupCollision)
    // console.log ('[DEBUG] otherGroupCollisionData', otherGroupCollisionData)
    if ((placedAfterFirst && placedBeforeSecond || otherGroupCollision) && group.findIndex(g => b.id === g.id) < 0)  {
      collisions.push (b)
    }
    console.log ('[DEBUG] collisions', JSON.stringify (collisions))
  }
  // groupsCollisionData.forEach(p => { console.log('[DEBUG] groupCollisionData', JSON.stringify(p)) })
  // console.log('isCollisionGroup', collisions.length > 0, JSON.stringify(collisions))
  return collisions.map(bp => bp.id!)
}

const isCollisionGroup4Section = (group: BoxPosition[], positions: BoxPosition[], groups: number[][], boxDef: BoxDef, side: string): number[] => {
  const inGroup = group.map(b => b.id!)
  // console.log ('[DEBUG] isCollisionGroup4Section - inGroup: ', JSON.stringify(inGroup))
  const indexes = []
  for (let i = 0; i < positions.length; i++) {
    if (inGroup.includes(positions[i].id!)) {
      indexes.push(i)
    }
  }
  // console.log('[DEBUG] isCollisionGroup4Section - indexes: ', JSON.stringify(indexes))

  const between1And2 = [...positions].slice(indexes[0] + 1, indexes[1])
  const between2And3 = (indexes[1] < indexes[2]) ? [...positions].slice(indexes[1] + 1, indexes[2]) : [...positions].slice(indexes[2] + 1, indexes[1])
  const between3And4 = [...positions].slice(indexes[2] + 1, indexes[3])
  // console.log('[DEBUG] isCollisionGroup4Section - boxes between (1): ', JSON.stringify(between1And2.map(b => b.id)), indexes[0], indexes[1])
  // console.log('[DEBUG] isCollisionGroup4Section - boxes between (2): ', JSON.stringify(between2And3.map(b => b.id)), indexes[1], indexes[2])
  // console.log('[DEBUG] isCollisionGroup4Section - boxes between (3): ', JSON.stringify(between3And4.map(b => b.id)), indexes[2], indexes[3])
  const boxesBetween = [...between1And2, ...between2And3, ...between3And4].filter(p => !inGroup.includes(p.id!))

  const firstPosition = positions[indexes[0]]
  const firstPosBox2 = positions[indexes[1]]
  const secondPosition = positions[indexes[2]]
  const secondPosBox2 = positions[indexes[3]]
  // console.log ('[DEBUG] isCollisionGroup4Section - boxesBetween', JSON.stringify (boxesBetween.map (p => p.id)))
  // console.log ('[DEBUG] isCollisionGroup4Section - positions', JSON.stringify (positions.map (p => p.id)))

  // Prepare structure for other group collision check
  const groupsCollisionData: CollisionData[] = []
  const groupMap: {[key: number]: CollisionData} = {}
  for (const g of groups) {
    const collisionData = {group: g, placedAfterFirst: false, placedBeforeSecond: false}
    groupsCollisionData.push (collisionData)
    for (const id of g) {
      groupMap[id] = collisionData
    }
  }

  const collisions = []
  for (const b of boxesBetween) {
    const depCheckFirst = checkDependencyOrder (firstPosition, b, boxDef, side)
    const depCheckFirst2 = checkDependencyOrder (firstPosBox2, b, boxDef, side)
    const depCheckSecond = checkDependencyOrder (b, secondPosition, boxDef, side)
    const depCheckSecond2 = checkDependencyOrder (b, secondPosBox2, boxDef, side)
    // console.log ('[DEBUG] isCollisionGroup4Section - depCheckFirst: ', firstPosition.id, JSON.stringify (depCheckFirst))
    // console.log ('[DEBUG] isCollisionGroup4Section - depCheckFirst2: ', firstPosBox2.id, JSON.stringify (depCheckFirst2))
    // console.log ('[DEBUG] isCollisionGroup4Section - depCheckSecond: ', secondPosition.id, JSON.stringify (depCheckSecond))
    // console.log ('[DEBUG] isCollisionGroup4Section - depCheckSecond2: ', secondPosBox2.id, JSON.stringify (depCheckSecond2))
    const placedAfterFirst = depCheckFirst.ok && (depCheckFirst.ordered[0].id === firstPosition.id)
     || depCheckFirst2.ok && (depCheckFirst2.ordered[0].id === firstPosBox2.id)
    const placedBeforeSecond = depCheckSecond.ok && (depCheckSecond.ordered[1].id === secondPosition.id)
     || depCheckSecond2.ok && (depCheckSecond2.ordered[1].id === secondPosBox2.id)
    // console.log('[DEBUG] isCollisionGroup4Section - placeAfterFirst: ', placedAfterFirst, depCheckFirst2.ok && (depCheckFirst2.ordered[0].id === firstPosBox2.id))
    // console.log('[DEBUG] isCollisionGroup4Section - placedBeforeSecond: ', placedBeforeSecond, depCheckSecond2.ok && (depCheckSecond2.ordered[0].id === secondPosBox2.id))

    // check collisions with other groups
    const otherGroupCollisionData = groupMap[b.id!]
    if (otherGroupCollisionData != null) {
      otherGroupCollisionData.placedAfterFirst = otherGroupCollisionData.placedAfterFirst || placedAfterFirst // logic sum
      otherGroupCollisionData.placedBeforeSecond = otherGroupCollisionData.placedBeforeSecond || placedBeforeSecond // logic sum
    }
    // const otherGroupCollision = groupsCollisionData.find (d => d.placedAfterFirst && d.placedBeforeSecond)
    // console.log ('[DEBUG] isCollisionGroup4Section - box:', b.id, {placedAfterFirst, placedBeforeSecond})
    // console.log ('[DEBUG] isCollisionGroup4Section ', JSON.stringify ({depCheckFirst, depCheckSecond}))
    // console.log ('[DEBUG] isCollisionGroup4Section - otherGroupCollision', JSON.stringify(otherGroupCollision))
    // console.log ('[DEBUG] isCollisionGroup4Section - otherGroupCollisionData', JSON.stringify(otherGroupCollisionData))

    if (placedAfterFirst && placedBeforeSecond) {
      collisions.push(b)
    }
    console.log ('[DEBUG] isCollisionGroup4Section - collisions ', JSON.stringify (collisions), b.id!)
  }

  // groupsCollisionData.forEach(p => { console.log('[DEBUG] groupCollisionData', JSON.stringify(p)) })
  // console.log('isCollisionGroup', collisions.length > 0, JSON.stringify(collisions))
  return collisions.map(bp => bp.id!)
}

export const checkDependencyOrder = (passive: BoxPosition, actor: BoxPosition, boxDef: BoxDef, side: string):
  {ok: boolean, ordered: BoxPosition[], distanceOk?: boolean} => {

  const actorIsBefore = Math.floor (actor.x!) < Math.floor (passive.x!)
  let actorIsHigher = Math.floor (actor.y!) < Math.floor (passive.y!)
  actorIsHigher = side === 'R' ? !actorIsHigher : actorIsHigher
  const distanceOk = (actorIsBefore) ? isDistanceOk(actor, passive, boxDef, side) : isDistanceOk(passive, actor, boxDef, side)
  const verticalDistanceOk = (actorIsHigher) ? isVerticalDistanceOk(actor, passive, boxDef) : isVerticalDistanceOk(passive, actor, boxDef)
  const horizontalIntersection = isHorizontalIntersection (actor, passive, boxDef)
  const verticalIntersection = isVerticalIntersection (actor, passive, boxDef)
  // console.log(`checkDependencyOrder - hDistanceOk: ${distanceOk}, vDistanceOk: ${verticalDistanceOk} [${actor.id}, ${passive.id}]`)
  // console.log(`checkDependencyOrder - horizontalIntersection: ${horizontalIntersection}, verticalIntersection: ${verticalIntersection}`)
  // console.log(`checkDependencyOrder - actorIsBefore: ${actorIsBefore}, actorIsHigher: ${actorIsHigher}`)
  if (horizontalIntersection && !(side === 'R' && distanceOk)) {
    // console.log(`checkDependencyOrder - horizontalIntersection - actorIsBefore: ${actorIsBefore}, distanceOk: ${distanceOk}, verticalDistanceOk: ${verticalDistanceOk}, [${actor.id}, ${passive.id}]`)
    let dOk = (verticalIntersection) ? distanceOk && verticalDistanceOk : distanceOk
    if ((actorIsBefore && actorIsHigher) || (!actorIsBefore && !actorIsHigher)) {
      dOk = false
    }
    return {ok: true, ordered: (actorIsBefore ? [actor, passive] : [passive, actor]), distanceOk: dOk}
  }

  if (verticalIntersection) {
    // console.log(`checkDependencyOrder - verticalIntersection - actorIsHigher: ${actorIsBefore}, distanceOk: ${distanceOk}, verticalDistanceOk: ${verticalDistanceOk} [${actor.id}, ${passive.id}]`)
    return {ok: true, ordered: (actorIsHigher ? [actor, passive] : [passive, actor]), distanceOk: distanceOk && verticalDistanceOk }
  }

  if (actorIsBefore && actorIsHigher) {
    // console.log(`checkDependencyOrder - actorIsBefore && actorIsHigher - distanceOk: ${distanceOk}, verticalDistanceOk: ${verticalDistanceOk} [${actor.id}, ${passive.id}]`)
    return {ok: true, ordered: [actor, passive], distanceOk: distanceOk && verticalDistanceOk}
  }

  if (!actorIsBefore && !actorIsHigher) {
    // console.log(`checkDependencyOrder - !actorIsBefore && !actorIsHigher - distanceOk: ${distanceOk}, verticalDistanceOk: ${verticalDistanceOk} [${actor.id}, ${passive.id}]`)
    return {ok: true, ordered: [passive, actor], distanceOk: distanceOk && verticalDistanceOk}
  }

  return {ok: false, ordered: []}
}
/**
 * Check if there is dependency inside the group for box placing order,
 * and they can be placed one only in one sequence (4 section, 4 boxes)
 */
export const checkDependencyOrder4section = (boxes: BoxPosition[], boxDef: BoxDef, side: string, template: Box, isSSL: boolean):
  {ok: boolean, ordered: BoxPosition[], withPusher?: boolean, sectionOrderList?: number[][]} => {
  const sorted = [...boxes].sort (alpha4Func)
  const angle1 = sorted[0].rotation! % 360
  let group1 = sorted.filter (b => b.rotation! % 360 === angle1)
  let group2 = sorted.filter (b => b.rotation! % 360 !== angle1)
  if (group1.length !== 4 && group1.length !== 2) {
    return {ok: false, ordered: [], withPusher: false}
  }
  const sectionOrderListNoPusher = [[1, 2, 3, 4], [3, 4, 1, 2]]
  const sectionOrderListPusher = [[1, 3, 2, 4], [2, 4, 1, 3]]
  const sameRotation = group1.length === 4
  const alignedGroups = getAlignedGroups (sorted, template.length!, template.width!, isSSL)
  console.log ('checkDependencyOrder4section - alignedGroups: ', JSON.stringify (alignedGroups))

  if (sameRotation) {
    // if all have same rotation, check alignment
    if (alignedGroups.horizontal[0].length !== 2 && alignedGroups.horizontal[0].length !== 4
      && alignedGroups.vertical[0].length !== 2 && alignedGroups.vertical[0].length !== 4) {
      return {ok: false, ordered: [], withPusher: false}
    }
    // only assign if 2 groups of 2 boxes
    if (alignedGroups.horizontal[0].length === 2 && alignedGroups.horizontal[1].length === 2) {
      // console.log ('checkDependencyOrder4section - handle alignedGroups horizontal')
      group1 = alignedGroups.horizontal[0]
      group2 = alignedGroups.horizontal[1]
    } else if (alignedGroups.vertical[0].length === 2 && alignedGroups.vertical[1].length === 2) {
      // console.log ('checkDependencyOrder4section - handle alignedGroups vertical')
      group1 = alignedGroups.vertical[0]
      group2 = alignedGroups.vertical[1]
    }
  }

  if (group1.length === 2 && group2.length === 2) {
    const pusher = alignedGroups.pusher
    const sectionOrder = (pusher) ? sectionOrderListPusher : sectionOrderListNoPusher

    // groups of 2 with different rotation or same alignment each
    group1 = group1.sort (alpha5Func)
    group2 = group2.sort (alpha5Func)
    console.log ('checkDependencyOrder4section - group1: ', JSON.stringify (group1))
    console.log ('checkDependencyOrder4section - group2: ', JSON.stringify (group2))
    const horIntersection1 = isHorizontalIntersection (group1[0], group2[0], boxDef)
    const horIntersection2 = isHorizontalIntersection (group1[0], group2[1], boxDef)
    const horIntersection3 = isHorizontalIntersection (group1[1], group2[1], boxDef)
    const horIntersection4 = isHorizontalIntersection (group1[1], group2[0], boxDef)
    const horIntersection = horIntersection1 || horIntersection2 || horIntersection3 || horIntersection4
    const isGroup1Before = isGroup1MoreLeftThanGroup2 (group1, group2, side)
    if (horIntersection) {
      console.log ('checkDependencyOrder4section - horIntersection: ', isGroup1Before, side)
      if (isGroup1Before) {
        return {
          ok: true,
          ordered: [group1[0], group1[1], group2[0], group2[1]],
          withPusher: pusher,
          sectionOrderList: sectionOrder
        }
      } else {
        return {
          ok: true,
          ordered: [group2[0], group2[1], group1[0], group1[1]],
          withPusher: pusher,
          sectionOrderList: sectionOrder
        }
      }
    }

    const verIntersection1 = isVerticalIntersection (group1[0], group2[0], boxDef)
    const verIntersection2 = isVerticalIntersection (group1[0], group2[1], boxDef)
    const verIntersection3 = isVerticalIntersection (group1[1], group2[1], boxDef)
    const verIntersection4 = isVerticalIntersection (group1[1], group2[0], boxDef)
    const verticalIntersection = verIntersection1 || verIntersection2 || verIntersection3 || verIntersection4
    const isGroup1Higher = isGroup1HigherThanGroup2 (group1, group2, side)
    if (verticalIntersection) {
      console.log ('checkDependencyOrder4section - verticalIntersection: ', isGroup1Before, isGroup1Higher, side)
      if (isGroup1Higher) {
        return {
          ok: true,
          ordered: [group1[0], group1[1], group2[0], group2[1]],
          withPusher: pusher,
          sectionOrderList: sectionOrder
        }
      } else {
        return {
          ok: true,
          ordered: [group2[0], group2[1], group1[0], group1[1]],
          withPusher: pusher,
          sectionOrderList: sectionOrder
        }
      }
    }

    console.log ('checkDependencyOrder4section - no intersection: ', isGroup1Before, isGroup1Higher)
    if (isGroup1Before) {
      return {
        ok: true,
        ordered: [group1[0], group1[1], group2[0], group2[1]],
        withPusher: pusher,
        sectionOrderList: sectionOrder
      }
    } else {
      return {
        ok: true,
        ordered: [group2[0], group2[1], group1[0], group1[1]],
        withPusher: pusher,
        sectionOrderList: sectionOrder
      }
    }
  } else {
    // same rotation, all aligned
    const group = [...boxes].sort (alpha5Func)
    console.log ('checkDependencyOrder4section -  all same rotation (alpha5 sorted): ', JSON.stringify (group), angle1, alignedGroups.pusher, isSSL)
    const horizontalIntersectionone_two = isHorizontalIntersection (group[0], group[1], boxDef)
    const horizontalIntersectionone_three = isHorizontalIntersection (group[0], group[2], boxDef)
    const horizontalIntersectionone_four = isHorizontalIntersection (group[0], group[3], boxDef)
    const horizontalIntersectionthree_four = isHorizontalIntersection (group[2], group[3], boxDef)
    const allAlignedHorizontal = horizontalIntersectionone_two && horizontalIntersectionone_three && horizontalIntersectionone_four

    const oneIsBeforetwo = Math.floor (group[0].x!) < Math.floor (group[1].x!)
    const oneIsBeforethree = Math.floor (group[0].y!) < Math.floor (group[2].y!)
    // console.log ('[DEBUG] checkDependency - horizontalIntersectionone_two: ', horizontalIntersectionone_two, group[0].id, group[1].id)
    // console.log ('[DEBUG] checkDependency - horizontalIntersectionone_three: ', horizontalIntersectionone_three, group[0].id, group[2].id)
    // console.log ('[DEBUG] checkDependency - horizontalIntersectionone_four: ', horizontalIntersectionone_four, group[0].id, group[3].id)
    // console.log ('[DEBUG] checkDependency - horizontalIntersectionthree_four: ', horizontalIntersectionthree_four, group[2].id, group[3].id)

    if (allAlignedHorizontal) {
      console.log ('checkDependencyOrder4section - allAlignedHorizontal: ', angle1, oneIsBeforetwo, oneIsBeforethree, isSSL)
      if (angle1 === 0) {
        if (oneIsBeforetwo && !oneIsBeforethree) {
          return {ok: true, ordered: group, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 180) {
        if (!oneIsBeforetwo && !oneIsBeforethree) {
          return {ok: true, ordered: [group[2], group[3], group[0], group[1]], withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 90) {
        return {ok: true, ordered: [group[2], group[3], group[0], group[1]], withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
      if (angle1 === 270) {
        return {ok: true, ordered: group, withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
    }
    if (horizontalIntersectionone_two && horizontalIntersectionthree_four && !horizontalIntersectionone_three) { // verify if needed
      console.log ('checkDependencyOrder4section - 2 horizontal groups (1): ', angle1, oneIsBeforetwo, oneIsBeforethree)
      if (angle1 === 0) {
        if (oneIsBeforetwo && !oneIsBeforethree) {
          return {ok: true, ordered: group, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 180) {
        if (!oneIsBeforetwo && oneIsBeforethree) {
          return {ok: true, ordered: group, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 90) {
        if (!oneIsBeforetwo && !oneIsBeforethree) {
          return {ok: true, ordered: [group[2], group[3], group[0], group[1]], withPusher: true, sectionOrderList: sectionOrderListPusher}
        }
        return {ok: true, ordered: group, withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
      if (angle1 === 270) {
        return {ok: true, ordered: group, withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
    }

    // Vertical orientations
    const verticalIntersectionone_two = isVerticalIntersection (group[0], group[1], boxDef)
    const verticalIntersectionone_three = isVerticalIntersection (group[0], group[2], boxDef)
    const verticalIntersectionthree_four = isVerticalIntersection (group[2], group[3], boxDef)

    const allAlignedVertical = verticalIntersectionone_two && verticalIntersectionone_three && verticalIntersectionthree_four
    // console.log ('[DEBUG] checkDependency - verticalIntersectionone_two: ', verticalIntersectionone_two, group[0].id, group[1].id)
    // console.log ('[DEBUG] checkDependency - verticalIntersectionone_three: ', verticalIntersectionone_three, group[0].id, group[2].id)
    // console.log ('[DEBUG] checkDependency - verticalIntersectionthree_four: ', verticalIntersectionthree_four, group[2].id, group[3].id)

    const oneisBeforetwo = Math.floor (group[0].y!) < Math.floor (group[1].y!)
    const oneisBeforethree = Math.floor (group[0].x!) < Math.floor (group[2].x!)

    if (allAlignedVertical) {
      console.log ('checkDependencyOrder4section - allAlignedVertical: ', angle1, oneisBeforetwo, oneisBeforethree)
      if (angle1 === 90) {
        const orderedGroup = (side === 'L') ? [group[2], group[3], group[0], group[1]] : group
        return {ok: true, ordered: orderedGroup, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
      }
      if (angle1 === 270) {
        if (oneisBeforetwo && !oneisBeforethree) {
          const orderedGroup = (side === 'L') ? group : [group[2], group[3], group[0], group[1]]
          return {ok: true, ordered: orderedGroup, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 0) {
        const orderedGroup = (side === 'L') ? [group[2], group[3], group[0], group[1]] : group
        return {ok: true, ordered: orderedGroup, withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
      if (angle1 === 180) {
        if (oneisBeforetwo && !oneisBeforethree) {
          const orderedGroup = (side === 'L') ? group : [group[2], group[3], group[0], group[1]]
          return {ok: true, ordered: orderedGroup, withPusher: true, sectionOrderList: sectionOrderListPusher}
        }
      }
    }
    if (verticalIntersectionone_two && verticalIntersectionthree_four && !verticalIntersectionone_three) { // verify if needed
      console.log ('checkDependencyOrder4section - 2 vertical groups (1): ', angle1, oneisBeforetwo, oneisBeforethree)
      if (angle1 === 90) {
        if (oneisBeforetwo && !oneisBeforethree) {
          return {ok: true, ordered: [group[3], group[1], group[2], group[0]], withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        } else {
          return {ok: true, ordered: group, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 270) {
        if (!oneisBeforetwo && !oneisBeforethree) {
          return {ok: true, ordered: [group[3], group[1], group[2], group[0]], withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        } else {
          return {ok: true, ordered: group, withPusher: false, sectionOrderList: sectionOrderListNoPusher}
        }
      }
      if (angle1 === 180) {
        if (oneisBeforetwo && !oneisBeforethree) {
          return {ok: true, ordered: [group[2], group[3], group[0], group[1]], withPusher: true, sectionOrderList: sectionOrderListPusher}
        }
        return {ok: true, ordered: group, withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
      if (angle1 === 0) {
        // !oneisBeforetwo && oneisBeforethree
        return {ok: true, ordered: group, withPusher: true, sectionOrderList: sectionOrderListPusher}
      }
    }
  }
  return {ok: false, ordered: [], withPusher: false}
}

export const checkDependencyOrderInline = (group1: BoxPosition[], group2: BoxPosition[], boxDef: BoxDef, side: string):
  {ok: boolean, orderedGroups: BoxPosition[][]} => {
  // console.log('checkDependencyOrderInline ', JSON.stringify(group1), JSON.stringify(group2), JSON.stringify(boxDef), side)
  let depOrder: BoxPosition[][] = []
  const isDependentBefore = []
  const isDependentAfter = []
  for (const box of group1) {
    for (const box2 of group2) {
      const depCheck = checkDependencyOrder(box, box2, boxDef, side)
      // console.log(`checkDependencyOrderInline box1: ${box.id}, box2: ${box2.id}, depCheck: ${JSON.stringify(depCheck)}`)
      if (depCheck.ok && depCheck.ordered[0].id === box.id) { // group1 before group2
        isDependentBefore.push(box)
      } else if (depCheck.ok && depCheck.ordered[1].id === box.id) { // group2 before group1
        isDependentAfter.push(box)
      }
    }
  }
  // console.log(`checkDependencyOrderInline - isDependentBefore: ${JSON.stringify(isDependentBefore)}, isDependentAfter: ${JSON.stringify(isDependentAfter)}`)
  const group1Sorted = [...group1].sort(alpha4Func)
  const group2Sorted = [...group2].sort(alpha4Func)
  if (isDependentBefore.length > isDependentAfter.length) {
    depOrder = [group1Sorted, group2Sorted]
  } else if (isDependentBefore.length < isDependentAfter.length) {
    depOrder = [group2Sorted, group1Sorted]
  }
  // console.log('checkDependencyOrderInline - depOrder: ', JSON.stringify(depOrder), depOrder.length)
  if (depOrder.length < 1) {
    return {ok: false, orderedGroups: []}
  }
  return {ok: true, orderedGroups: depOrder}
}

/**
 * Checks if group1 is located higher than group2 (from robots perspective)
 * by comparing the middle Y coordinates of the group.
 */
const isGroup1HigherThanGroup2 = (group1: BoxPosition[], group2: BoxPosition[], side: string): boolean => {
  const group1MiddleY = Math.floor ((group1[0].y! + group1[1].y!) / 2)
  const group2MiddleY = Math.floor ((group2[0].y! + group2[1].y!) / 2)
  // console.log ('isGroup1HigherThanGroup2: ', group1MiddleY, group2MiddleY, side)
  return side === 'L' ? group1MiddleY < group2MiddleY : group1MiddleY > group2MiddleY
}

/**
 * Checks if group1 is located more left than group2 (from robots perspective)
 * by comparing the middle X coordinates of the group.
 */
const isGroup1MoreLeftThanGroup2 = (group1: BoxPosition[], group2: BoxPosition[], side: string): boolean => {
  const group1MiddleX = Math.floor ((group1[0].x! + group1[1].x!) / 2)
  const group2MiddleX = Math.floor ((group2[0].x! + group2[1].x!) / 2)
  // console.log ('isGroup1MoreLeftThanGroup2: ', group1MiddleX, group2MiddleX, side)
  return group1MiddleX < group2MiddleX
}

// @ts-ignore
export const getAlignedGroups = (boxes: BoxPosition[], templateLength: number, templateWidth: number, isSSL: boolean):
  {horizontal: BoxPosition[][], vertical: BoxPosition[][], pusher: boolean} => {
  const groupVertical1: BoxPosition[] = []
  const groupVertical2: BoxPosition[] = []
  const groupHorizontal1: BoxPosition[] = []
  const groupHorizontal2: BoxPosition[] = []
  for (const b1 of boxes) {
    for (const b2 of boxes) {
      if (b1.id === b2.id) {
        continue
      }
      if (b1.rotation !== b2.rotation) {
        continue
      }

      const angle1 = b1.rotation!
      const w = Math.abs (
        templateLength * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
        templateWidth * Math.round (Math.sin (angle1 / 180 * Math.PI))
      )
      const h = Math.abs (
        templateWidth * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
        templateLength * Math.round (Math.sin (angle1 / 180 * Math.PI))
      )

      if (equal (b1.x!, b2.x!)) {
        const verticalDistance = Math.abs (b1.y! - b2.y!)
        const distanceOk = isSSL ? equal (verticalDistance, h) : equal (verticalDistance, w)
        if (distanceOk) {
          if (groupVertical1.length === 0 || equal (groupVertical1[0].x!, b1.x!)) {
            if (groupVertical1.indexOf (b1) < 0) {
              groupVertical1.push (b1)
            }
            if (groupVertical1.indexOf (b2) < 0) {
              groupVertical1.push (b2)
            }
          } else if (groupVertical2.length === 0 || equal (groupVertical2[0].x!, b1.x!)) {
            if (groupVertical2.indexOf (b1) < 0) {
              groupVertical2.push (b1)
            }
            if (groupVertical2.indexOf (b2) < 0) {
              groupVertical2.push (b2)
            }
          }
        }
      }
      if (equal (b1.y!, b2.y!)) {
        const horizontalDistance = Math.abs (b1.x! - b2.x!)
        const distanceOk = isSSL ? equal (horizontalDistance, w) : equal (horizontalDistance, h)
        if (distanceOk) {
          if (groupHorizontal1.length < 1 || equal (groupHorizontal1[0].y!, b1.y!)) {
            if (groupHorizontal1.indexOf (b1) < 0) {
              groupHorizontal1.push (b1)
            }
            if (groupHorizontal1.indexOf (b2) < 0) {
              groupHorizontal1.push (b2)
            }
          } else if (groupHorizontal2.length < 1 || equal (groupHorizontal2[0].y!, b1.y!)) {
            if (groupHorizontal2.indexOf (b1) < 0) {
              groupHorizontal2.push (b1)
            }
            if (groupHorizontal2.indexOf (b2) < 0) {
              groupHorizontal2.push (b2)
            }
          }
        }
      }
    }
  }
  // pusher:
  // - vertically aligned + 0/180 degrees
  // - horizontally aligned + 90/270 degrees
  const all4HorizontallyAligned = groupHorizontal1.length === 4
  const all4VerticallyAligned = groupVertical1.length === 4
  const twoGroupsHorizontallyAligned = groupHorizontal1.length === 2 && groupHorizontal2.length === 2
  const twoGroupsVerticallyAligned = groupVertical1.length === 2 && groupVertical2.length === 2
  const horizontalAndVertical = groupHorizontal1.length >= 2 && groupHorizontal2.length === 0 && groupVertical1.length >= 2 && groupVertical2.length === 0

  if (all4HorizontallyAligned) {
    const isPusherAligned = groupHorizontal1[0].rotation === 90 || groupHorizontal1[0].rotation === 270
    return {horizontal: [groupHorizontal1, groupHorizontal2], vertical: [groupVertical1, groupVertical2], pusher: isPusherAligned}
  }

  if (all4VerticallyAligned) {
    const isPusherAligned = groupVertical1[0].rotation === 0 || groupVertical1[0].rotation === 180
    return {horizontal: [groupHorizontal1, groupHorizontal2], vertical: [groupVertical1, groupVertical2], pusher: isPusherAligned}
  }

  if (horizontalAndVertical) {
    const horPusherAligned = (groupHorizontal1[0].rotation === 90 || groupHorizontal1[0].rotation === 270)
    const verPusherAligned = (groupVertical1[0].rotation === 0 || groupVertical1[0].rotation === 180)
    if (horPusherAligned === verPusherAligned) {
      return {horizontal: [groupHorizontal1, groupHorizontal2], vertical: [groupVertical1, groupVertical2], pusher: horPusherAligned}
    }
    return {horizontal: [[], []], vertical: [[], []], pusher: false}
  }
  const group1PusherAligned = (twoGroupsHorizontallyAligned && (groupHorizontal1[0].rotation === 90 || groupHorizontal1[0].rotation === 270))
    || (twoGroupsVerticallyAligned && (groupVertical1[0].rotation === 0 || groupVertical1[0].rotation === 180))

  const group2PusherAligned = (twoGroupsHorizontallyAligned && (groupHorizontal2[0].rotation === 90 || groupHorizontal2[0].rotation === 270))
    || (twoGroupsVerticallyAligned && (groupVertical2[0].rotation === 0 || groupVertical2[0].rotation === 180))

  if (group1PusherAligned !== group2PusherAligned) {
    return {horizontal: [[], []], vertical: [[], []], pusher: false}
  }

  return {horizontal: [groupHorizontal1, groupHorizontal2], vertical: [groupVertical1, groupVertical2], pusher: group1PusherAligned}
}

/**
 * Only returns aligned boxes in line on conveyor
 */
export const getAlignedGroupsInline = (boxes: BoxPosition[], templateLength: number, templateWidth: number, isSSL: boolean)
  : {horizontal: BoxPosition[], vertical: BoxPosition[], rest: BoxPosition[]} => {
  const groupVertical: BoxPosition[] = []
  const groupHorizontal: BoxPosition[] = []
  let rest: BoxPosition[] = []
  // console.log('getAlignedGroupsInline - boxes: ', JSON.stringify(boxes))
  for (const b1 of boxes) {
    for (const b2 of boxes) {
      if (b1.id === b2.id) {
        continue
      }
      if (b1.rotation !== b2.rotation) {
        continue
      }
      const angle1 = b1.rotation!
      const w = Math.abs (
        templateLength * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
        templateWidth * Math.round (Math.sin (angle1 / 180 * Math.PI))
      )
      const h = Math.abs (
        templateWidth * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
        templateLength * Math.round (Math.sin (angle1 / 180 * Math.PI))
      )
      const distanceOnConveyor = (isSSL) ? templateLength : templateWidth
      // console.log ('distanceOnConveyor: ', distanceOnConveyor)
      if (equal (b1.x!, b2.x!)) {
        const verticalDistance = Math.abs (b1.y! - b2.y!)
        const distanceOk = isSSL ? isAligned (verticalDistance, h) : isAligned (verticalDistance, w)
        // console.log ('getAlignedGroupsInline - verticalAligned: ', verticalDistance, distanceOk, equal (verticalDistance, distanceOnConveyor), h, w)
        if (distanceOk && isAligned (verticalDistance, distanceOnConveyor)) {
          if (groupVertical.indexOf (b1) < 0) {
            groupVertical.push (b1)
          }
          if (groupVertical.indexOf (b2) < 0) {
            groupVertical.push (b2)
          }
        }
      }
      if (equal (b1.y!, b2.y!)) {
        const horizontalDistance = Math.abs (b1.x! - b2.x!)
        const distanceOk = isSSL ? isAligned (horizontalDistance, w) : isAligned (horizontalDistance, h)
        // console.log ('getAlignedGroupsInline - horizontalAligned: ', horizontalDistance, distanceOk, equal (horizontalDistance, distanceOnConveyor), h, w)
        if (distanceOk && isAligned (horizontalDistance, distanceOnConveyor)) {
          if (groupHorizontal.indexOf (b1) < 0) {
            groupHorizontal.push (b1)
          }
          if (groupHorizontal.indexOf (b2) < 0) {
            groupHorizontal.push (b2)
          }
        }
      }
    }
  }
  if (groupHorizontal.length > 1) {
    rest = boxes.filter(b => !groupHorizontal.includes(b))
  } else if (groupVertical.length > 1) {
    rest = boxes.filter(b => !groupVertical.includes(b))
  }
  return {horizontal: groupHorizontal, vertical: groupVertical, rest: rest}
}

const SIDE_MARGIN = 1

export const topSide2 = (box: BoxValue | BoxPosition, boxDef: BoxDef): number => {
  return box.y! - (box.rotation! % 180 === 0 ? (boxDef.height) / 2 : (boxDef.width / 2)) + SIDE_MARGIN
}

export const bottomSide2 = (box: BoxValue | BoxPosition, boxDef: BoxDef): number => {
  return box.y! + (box.rotation! % 180 === 0 ? (boxDef.height) / 2 : (boxDef.width / 2)) - SIDE_MARGIN
}

export const leftSide2 = (box: BoxValue | BoxPosition, boxDef: BoxDef): number => {
  return box.x! - (box.rotation! % 180 === 0 ? (boxDef.width) / 2 : (boxDef.height / 2)) + SIDE_MARGIN
}

export const rightSide2 = (box: BoxValue | BoxPosition, boxDef: BoxDef): number => {
  return box.x! + (box.rotation! % 180 === 0 ? (boxDef.width) / 2 : (boxDef.height / 2)) - SIDE_MARGIN
}

export const isHorizontalIntersection = (box1: BoxPosition, box2: BoxPosition, boxDef: BoxDef): boolean => {
  return Math.floor (topSide2 (box1, boxDef)) < Math.floor (bottomSide2 (box2, boxDef))
    && Math.floor (bottomSide2 (box1, boxDef)) + SIDE_MARGIN > Math.floor (topSide2 (box2, boxDef))
}

export const isVerticalIntersection = (box1: BoxPosition, box2: BoxPosition, boxDef: BoxDef): boolean => {
  return Math.floor (leftSide2 (box1, boxDef)) < Math.floor (rightSide2 (box2, boxDef))
    && Math.floor (rightSide2 (box1, boxDef)) > Math.floor (leftSide2 (box2, boxDef))
}

export const isDistanceOk = (box1: BoxValue | BoxPosition, box2: BoxValue | BoxPosition, boxDef: BoxDef, side: string): boolean => {
  const distance = (box1.rotation! % 180 === 0) ? boxDef.width / 2 : boxDef.height / 2
  const distanceOk = rightSide2(box1, boxDef) + distance < leftSide2(box2, boxDef)
  return distanceOk && !isMovementIntersecting(box1, box2, boxDef, side)
}

export const isVerticalDistanceOk = (box1: BoxValue | BoxPosition, box2: BoxValue | BoxPosition, boxDef: BoxDef): boolean => {
  const distance = (box1.rotation! % 180 === 0) ? boxDef.height / 2 : boxDef.width / 2
  return bottomSide2(box1, boxDef) + distance < topSide2(box2, boxDef)
}

/**
 * rotation adjustment only needed for QP
 * @param b
 * @param rotation
 */
export const getGripperRangesXY = (b: Borders, rotation: number = 0): Range[] => {
  const left = b.left * utils.projX (rotation) + b.right * utils.projY (rotation)
  const right = b.right * utils.projX (rotation) + b.left * utils.projY (rotation)
  const top = b.top * utils.projX (rotation) + b.bottom * utils.projY (rotation)
  const bottom = b.bottom * utils.projX (rotation) + b.top * utils.projY (rotation)

  const rangeX = {a: left, b: right}
  const rangeY = {a: top, b: bottom}
  return [rangeX, rangeY]
}

export const getGripperRangesXYForSections = (b: Borders, rotation: number, sections: number[]): Range[] => {
  const normal = sections.every (a => [1, 2].includes (a))
  // console.log('getGripperRangesXYForSections: ', JSON.stringify(b), JSON.stringify(sections), rotation, normal)
  let left = b.left * utils.projX (rotation) + b.right * utils.projY (rotation)
  let right = b.right * utils.projX (rotation) + b.left * utils.projY (rotation)
  let top = b.top * utils.projX (rotation) + b.bottom * utils.projY (rotation)
  let bottom = b.bottom * utils.projX (rotation) + b.top * utils.projY (rotation)
  // console.log(`getGripperRangesXYForSections (1) - left: ${left}, right: ${right}, top: ${top}, bottom: ${bottom}`)
  if (normal) {
    const diff = ((bottom - top) / 2) * utils.projX(rotation) + ((right - left) / 2) * utils.projY (rotation)
    if (rotation === 0) {
      top = top + diff
    } else if (rotation === 90) {
      left = left + diff
    } else if (rotation === 180) {
      bottom = bottom - diff
    } else if (rotation === 270) {
      right = right - diff
    }
  } else {
    const diff = ((right - left) / 2) * utils.projX(rotation) + ((bottom - top) / 2) * utils.projY (rotation)
    if (rotation === 0) {
      right = right - diff
    } else if (rotation === 90) {
      top = top + diff
    } else if (rotation === 180) {
      left = left + diff
    } else if (rotation === 270) {
      bottom = bottom - diff
    }
  }
  // console.log(`getGripperRangesXYForSections (2) - left: ${left}, right: ${right}, top: ${top}, bottom: ${bottom}`)
  const rangeX = {a: left, b: right}
  const rangeY = {a: top, b: bottom}
  return [rangeX, rangeY]
}

export const getGripperRangesXYTP2Boxes = (b: Borders, rotation: number): Range[] => {
  let left = b.left
  let right = b.right
  let top = b.top
  let bottom = b.bottom

  const diff = (rotation % 180 === 0) ? (b.right - b.left) / 3 : (b.bottom - b.top) / 3
  if (rotation === 0) {
    right = right - diff
  } else if (rotation === 90) {
    top = top + diff
  } else if (rotation === 180) {
    left = left + diff
  } else if (rotation === 270) {
    bottom = bottom - diff
  }
  // console.log(`getGripperRangesXYTP2Boxes (2) - left: ${left}, right: ${right}, top: ${top}, bottom: ${bottom}`)
  const rangeX = {a: left, b: right}
  const rangeY = {a: top, b: bottom}
  return [rangeX, rangeY]
}

export const getBoxRangesXY = (p: BoxPosition, boxDef: BoxDef): Range[] => {
  const width = fullWidth (boxDef) * utils.projX (p.rotation!) + fullHeight (boxDef) * utils.projY (p.rotation!)
  const height = fullHeight (boxDef) * utils.projX (p.rotation!) + fullWidth (boxDef) * utils.projY (p.rotation!)
  const rangeX = {a: p.x! - width / 2, b: p.x! + width / 2}
  const rangeY = {a: p.y! - height / 2, b: p.y! + height / 2}
  // console.log('[DEBUG] range' , JSON.stringify([rangeX, rangeY]))
  return [rangeX, rangeY]
}

export const isRangesOverlap = (r1: Range, r2: Range, tolerance: number = 1): boolean => {
  // TODO: refactor to application settings
  if (tolerance == null) {
    tolerance = 1
  }
  const a = r2.a > r1.a && (r2.a + tolerance) < r1.b
  const b = r1.a > r2.a && (r1.a + tolerance) < r2.b
  return a || b || (r1.a === r2.a) || (r1.b === r2.b)
}

export const getBoxesInBetween = (group: BoxPosition[], positions: BoxPosition[])
  : {boxesBetween: BoxPosition[], betweenPositions: BoxPosition[]} => {
  // console.log ('POSITIONS: ', JSON.stringify (positions.map (p => p.id!)))
  const inGroup = group.map (b => b.id)
  // get all indices
  const gbIndices: number[] = []
  for (let i = 0; i < positions.length; i++) {
    if (inGroup.includes (positions[i].id)) {
      gbIndices.push (i)
    }
  }
  // check positions in between indices
  let boxesBetween: BoxPosition[] = []
  const betweenPositions: BoxPosition[] = []
  for (let i = 0; i < gbIndices.length - 1; i++) {
    const first = gbIndices[i]
    const second = gbIndices[i + 1]
    const between = [...positions].slice (first + 1, second)
    if (between && between.length > 0) {
      // console.log ('BETWEEN INDICES: ', first, second, JSON.stringify(between.map (b => b.id)))
      boxesBetween = [...boxesBetween, ...between]
    }
  }
  if (boxesBetween.length > 0) {
    betweenPositions.push(positions[gbIndices[0]])
    betweenPositions.push(positions[gbIndices[gbIndices.length - 1]])
  }
  // console.log ('BOXES BETWEEN: ', JSON.stringify (boxesBetween.map (b => b.id)))
  return {boxesBetween: boxesBetween, betweenPositions: betweenPositions}
}

export const pusherPrepPossible = (box: Box | BoxDef, settings: MachineData): boolean => {
  if (!settings.pusher) {
    return false
  }
  let widthOnConv = box.height!
  if ('length' in box) { // diff between Box and BoxDef
    widthOnConv = settings.boxOrientationOnConveyor === BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING ? box.width! : box.length!
  }
  const maxWidth = settings.pusherMaxBoxWidth ? settings.pusherMaxBoxWidth : defaultPusherMaxBoxWidth
  return widthOnConv <= maxWidth
}

export const isMovementIntersecting = (box1: BoxValue | BoxPosition, box2: BoxValue | BoxPosition, boxDef: BoxDef, side: string): boolean => {
  const rs1 = (side === 'L') ? rightSide2(box1, boxDef) : leftSide2(box1, boxDef)
  const ls1 = (side === 'L') ? leftSide2(box1, boxDef) : rightSide2(box1, boxDef)
  const ts1 = topSide2(box1, boxDef)
  const bs1 = bottomSide2(box1, boxDef)
  const length = 999
  const angle = (side === 'L') ? 315 * (Math.PI / 180) : 45 * (Math.PI / 180)
  // console.log(`isIntersection - box1 - left: ${leftSide2(box1, boxDef)}, right: ${rightSide2(box1, boxDef)}, top: ${ts1}, bottom: ${bs1} (rotation: ${box1.rotation})`)

  const start1 = { x: rs1, y: ts1 }
  const end1 = { x: rs1 + Math.cos(angle) * length, y: ts1 - Math.sin(angle) * length}
  const start2 = { x: ls1, y: bs1 }
  const end2 = { x: ls1 + Math.cos(angle) * length, y: bs1 - Math.sin(angle) * length}
  const rays = [
    [start1, end1],
    [start2, end2]
  ]
  // console.log('isIntersection - rays: ', JSON.stringify(rays))
  const left2 = leftSide2(box2, boxDef)
  const right2 = rightSide2(box2, boxDef)
  const top2 = topSide2(box2, boxDef)
  const bottom2 = bottomSide2(box2, boxDef)
  const points = [
    {x: left2, y: top2}, {x: left2, y: bottom2},
    {x: right2, y: top2}, {x: right2, y: bottom2}
  ]
  // console.log('isIntersection - points: ', JSON.stringify(points))
  for (const r of rays) {
    for (let i = 0; i < points.length; i++) {
      const check = intersects (r[0], r[1], points[i], points[(i + 1) % points.length])
      if (check) {
        return true
      }
    }
  }
  return false
}

const intersects = (a: any, b: any, c: any, d: any): boolean => {
  const tTop = (d.x - c.x) * (a.y - c.y) - (d.y - c.y) * (a.x - c.x)
  const uTop = (c.y - a.y) * (a.x - b.x) - (c.x - a.x) * (a.y - b.y)
  const bottom = (d.y - c.y) * (b.x - a.x) - (d.x - c.x) * (b.y - a.y)
  if (bottom !== 0) {
    const t = tTop / bottom
    const u = uTop / bottom
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return true
    }
  }
  return false
}
